import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DataGrid, { Column, Paging, Pager } from "devextreme-react/data-grid";
import Button from "devextreme-react/button";
import Popup from "devextreme-react/popup";
import notify from "devextreme/ui/notify";
import axios, { AxiosError } from "axios";
import { PageContainer, HeaderContainer, Title } from "./TablaActivos.styles";
import { envasesService } from "../../services/envasesService";
import { productosService } from "../../services/productosService";
import { Envase } from "../../dtos/Envase";
import { Productos } from "../../dtos/Productos";
import { ApiError } from "../../services/apiError";
import ProductoAutocomplete from "./ProductoAutocomplete";

const handleApiError = (err: unknown): ApiError => {
  if (axios.isAxiosError(err)) {
    const error = err as AxiosError<ApiError>;
    return {
      message:
        error.response?.data?.message || "Ocurrió un error en el servidor.",
      status: error.response?.status,
      details: error.response?.data?.details || null,
    };
  }
  return { message: "Ocurrió un error inesperado." };
};

const CompletarEnvases: React.FC = () => {
  const navigate = useNavigate();
  const [envases, setEnvases] = useState<Envase[]>([]);
  const [loading, setLoading] = useState(true);
  const [popupVisible, setPopupVisible] = useState(false);
  const [selected, setSelected] = useState<Envase | null>(null);
  const [codigoProducto, setCodigoProducto] = useState<number | null>(null);
  const [producto, setProducto] = useState<Productos | null>(null);
  const [saving, setSaving] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await envasesService.getEnvasesSinProducto();
      setEnvases(data ?? []);
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const openAsignar = (envase: Envase) => {
    setSelected(envase);
    setCodigoProducto(null);
    setProducto(null);
    setPopupVisible(true);
  };

  const handleProductoChange = async (codigo: number | null) => {
    setCodigoProducto(codigo);
    if (!codigo) {
      setProducto(null);
      return;
    }
    try {
      const prod = await productosService.getByCodigo(codigo);
      setProducto(prod ?? null);
    } catch (err) {
      setProducto(null);
      notify(handleApiError(err).message, "error", 3000);
    }
  };

  const productoCompleto =
    !!producto &&
    !!producto.codigoCuerpo &&
    !!producto.codigoCapuchon &&
    !!producto.codigoCuello &&
    !!producto.codigoEstarcido &&
    !!producto.codigoOjiva &&
    producto.idValvula != null;

  const handleAsignar = async () => {
    if (!selected || !codigoProducto) {
      notify("Seleccione un producto.", "warning", 2500);
      return;
    }
    if (!productoCompleto) {
      notify(
        "El producto no tiene la configuración de catálogos completa.",
        "warning",
        3000,
      );
      return;
    }
    setSaving(true);
    try {
      await envasesService.asignarProducto(selected.id, {
        codigoProducto,
      });
      notify("Producto asignado al envase.", "success", 2500);
      setPopupVisible(false);
      await loadData();
    } catch (err) {
      notify(handleApiError(err).message, "error", 3000);
    } finally {
      setSaving(false);
    }
  };

  const ActionsCell = (cellData: { data: Envase }) => (
    <Button
      icon="check"
      text="Asignar producto"
      stylingMode="contained"
      type="default"
      onClick={() => openAsignar(cellData.data)}
    />
  );

  return (
    <PageContainer>
      <div style={{ marginBottom: 15 }}>
        <Button
          icon="chevronleft"
          text="Regresar"
          stylingMode="outlined"
          type="default"
          onClick={() => navigate("/consulta-activos")}
        />
      </div>
      <HeaderContainer>
        <Title>Completar envases sin producto</Title>
      </HeaderContainer>

      <DataGrid
        dataSource={envases}
        keyExpr="id"
        showBorders
        columnAutoWidth
        wordWrapEnabled
        loadPanel={{ enabled: loading }}
        noDataText={loading ? "Cargando..." : "No hay envases sin producto"}
      >
        <Paging defaultPageSize={20} />
        <Pager showInfo />
        <Column dataField="numeroSerie" caption="Serie" />
        <Column dataField="fabricante" caption="Fabricante" />
        <Column dataField="modelo" caption="Modelo" />
        <Column dataField="tipoDeEnvase" caption="Tipo" />
        <Column dataField="fechaRegistro" caption="Registro" dataType="datetime" />
        <Column caption="Acción" alignment="center" width={190} cellRender={ActionsCell} />
      </DataGrid>

      <Popup
        visible={popupVisible}
        onHiding={() => setPopupVisible(false)}
        showTitle
        title={`Asignar producto a ${selected?.numeroSerie ?? ""}`}
        width={520}
        height="auto"
      >
        <div style={{ padding: 20 }}>
          <label style={{ display: "block", marginBottom: 8, fontWeight: 600 }}>
            Producto
          </label>
          <ProductoAutocomplete
            value={codigoProducto}
            onChange={handleProductoChange}
          />
          {producto && (
            <div style={{ marginTop: 16, fontSize: 13, color: "#42526E" }}>
              <p style={{ margin: "4px 0" }}>
                <strong>Descripción:</strong> {producto.descripcion ?? "—"}
              </p>
              <p style={{ margin: "4px 0" }}>
                <strong>Cuerpo:</strong> {producto.codigoCuerpo ?? "—"}{" "}
                <strong>Capuchón:</strong> {producto.codigoCapuchon ?? "—"}{" "}
                <strong>Cuello:</strong> {producto.codigoCuello ?? "—"}
              </p>
              <p style={{ margin: "4px 0" }}>
                <strong>Estarcido:</strong> {producto.codigoEstarcido ?? "—"}{" "}
                <strong>Ojiva:</strong> {producto.codigoOjiva ?? "—"}{" "}
                <strong>Válvula:</strong> {producto.valvula ?? producto.idValvula ?? "—"}
              </p>
              {!productoCompleto && (
                <p style={{ color: "#dc2626" }}>
                  El producto no tiene la configuración de catálogos completa.
                </p>
              )}
            </div>
          )}
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 12,
              marginTop: 24,
            }}
          >
            <Button text="Cancelar" onClick={() => setPopupVisible(false)} />
            <Button
              text={saving ? "Guardando..." : "Asignar"}
              type="default"
              stylingMode="contained"
              onClick={handleAsignar}
              disabled={saving || !productoCompleto}
            />
          </div>
        </div>
      </Popup>
    </PageContainer>
  );
};

export default CompletarEnvases;
